import React, { useEffect, useState } from 'react';
import './GoogleTranslate.css';

const SUPPORTED_LANGUAGES = [
  { code: 'mr', label: 'मराठी', englishLabel: 'Marathi' },
  { code: 'en', label: 'English', englishLabel: 'English' },
  { code: 'hi', label: 'हिंदी', englishLabel: 'Hindi' },
];

export const clearGoogleTranslateCookies = () => {
  if (typeof document === 'undefined') return;
  const hostname = window.location.hostname;
  const domains = ['', hostname, `.${hostname}`];

  // If not localhost or IP, extract all parent domains (e.g. .deulgaongada.in)
  const isIpOrLocalhost = hostname === 'localhost' || /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
  if (!isIpOrLocalhost) {
    const parts = hostname.split('.');
    for (let i = 0; i < parts.length - 1; i++) {
      const parent = parts.slice(i).join('.');
      if (parent.includes('.')) {
        domains.push(parent, `.${parent}`);
      }
    }
  }

  const uniqueDomains = Array.from(new Set(domains));
  const paths = ['/', window.location.pathname];

  uniqueDomains.forEach((domain) => {
    paths.forEach((path) => {
      const domainAttr = domain ? `; domain=${domain}` : '';
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}${domainAttr};`;
      document.cookie = `googtrans=; max-age=0; path=${path}${domainAttr};`;
    });
  });
};

export const setGoogleTranslateCookie = (langCode) => {
  if (typeof document === 'undefined') return;
  clearGoogleTranslateCookies();

  if (langCode === 'mr') {
    return;
  }

  const cookieVal = `/mr/${langCode}`;
  const hostname = window.location.hostname;

  // Set host-only cookie on root path
  document.cookie = `googtrans=${cookieVal}; path=/;`;

  // For domain setups (e.g. *.deulgaongada.in), also set on root domain for subdomain sharing
  const isIpOrLocalhost = hostname === 'localhost' || /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
  if (!isIpOrLocalhost) {
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      const rootDomain = '.' + parts.slice(-2).join('.');
      document.cookie = `googtrans=${cookieVal}; path=/; domain=${rootDomain};`;
    }
  }
};

const getStoredLanguage = () => {
  if (typeof document === 'undefined') return 'mr';
  const raw = localStorage.getItem('portal_language');
  if (raw === 'mr') {
    return 'mr';
  }
  const match = document.cookie.match(/(?:^|;\s*)googtrans=\/(?:mr|auto)\/([a-z]{2})/i);
  if (match && match[1]) {
    return match[1].toLowerCase();
  }
  return raw || 'mr';
};

export default function GoogleTranslate({ variant = 'default' }) {
  const [currentLang, setCurrentLang] = useState(getStoredLanguage);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const stored = getStoredLanguage();
    if (stored === 'mr') {
      // Ensure leftover cookies don't trigger auto-translation when Marathi is intended
      clearGoogleTranslateCookies();
    }

    // Check if Google Translate script is already added
    if (!window.googleTranslateElementInit) {
      window.googleTranslateElementInit = () => {
        if (window.google?.translate?.TranslateElement) {
          try {
            new window.google.translate.TranslateElement(
              {
                pageLanguage: 'mr',
                includedLanguages: 'mr,en,hi',
                layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
                autoDisplay: false,
              },
              'google_translate_element'
            );
          } catch (e) {
            console.error('Google Translate Init Error:', e);
          }
        }
      };

      const existingScript = document.getElementById('google-translate-script');
      if (!existingScript) {
        const script = document.createElement('script');
        script.id = 'google-translate-script';
        script.type = 'text/javascript';
        script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
        script.async = true;
        document.body.appendChild(script);
      }
    }
  }, []);

  const changeLanguage = (langCode) => {
    if (langCode === currentLang) {
      setIsOpen(false);
      return;
    }

    setIsOpen(false);
    setCurrentLang(langCode);
    localStorage.setItem('portal_language', langCode);

    if (langCode === 'mr') {
      // Reverting to native page language requires clearing cookies and reloading
      clearGoogleTranslateCookies();
      window.location.reload();
      return;
    }

    // Setting a non-Marathi language (en or hi)
    setGoogleTranslateCookie(langCode);

    // If switching from an already translated language (e.g. en -> hi or hi -> en),
    // a page reload is required so Google Translate translates cleanly from native Marathi
    // rather than mutating already-translated DOM text nodes and causing React reconciliation crashes.
    if (currentLang !== 'mr') {
      window.location.reload();
      return;
    }

    // First translation from native Marathi: try instant combo dispatch
    const combo = document.querySelector('.goog-te-combo');
    if (combo) {
      combo.value = langCode;
      if (typeof combo.onchange === 'function') {
        combo.onchange();
      }
      combo.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      window.location.reload();
    }
  };

  return (
    <div className={`google-translate-widget ${variant}`} aria-label="भाषा निवडा / Choose Language">
      {/* Hidden container for Google Translate element */}
      <div id="google_translate_element" style={{ display: 'none' }} />

      <div className="gt-quick-toggle">
        <span className="gt-globe-icon" aria-hidden="true">🌐</span>
        <button
          type="button"
          className={`gt-btn ${currentLang === 'mr' ? 'active' : ''}`}
          onClick={() => changeLanguage('mr')}
          title="मराठी (Default)"
        >
          मराठी
        </button>
        <span className="gt-divider">|</span>
        <button
          type="button"
          className={`gt-btn ${currentLang === 'en' ? 'active' : ''}`}
          onClick={() => changeLanguage('en')}
          title="English Translation"
        >
          English
        </button>

        <button
          type="button"
          className="gt-dropdown-trigger"
          onClick={() => setIsOpen(prev => !prev)}
          aria-expanded={isOpen}
          title="अधिक भाषा / More Languages"
        >
          ▾
        </button>
      </div>

      {isOpen && (
        <div className="gt-dropdown-menu">
          {SUPPORTED_LANGUAGES.map(lang => (
            <button
              key={lang.code}
              type="button"
              className={`gt-dropdown-item ${currentLang === lang.code ? 'selected' : ''}`}
              onClick={() => changeLanguage(lang.code)}
            >
              <span className="gt-lang-native">{lang.label}</span>
              <span className="gt-lang-sub">({lang.englishLabel})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
